"use client";

import React, { useEffect, useState } from "react";
import { Icon } from "./icon";
import { cn } from "./lib/utils";

export function ScrollingTestimonials({
	items,
	direction = "left",
	speed = "fast",
	pauseOnHover = true,
	className,
}: {
	items: {
		body: string;
		author?: {
			name: string;
			handle: string;
			imageUrl: string;
			xVerified?: boolean;
			logoUrl?: string;
		};
	}[];
	direction?: "left" | "right";
	speed?: "fast" | "normal" | "slow";
	pauseOnHover?: boolean;
	className?: string;
}) {
	const containerRef = React.useRef<HTMLDivElement>(null);
	const scrollerRef = React.useRef<HTMLUListElement>(null);
	const [start, setStart] = useState(false);

	useEffect(() => {
		addAnimation();
	}, []);

	function addAnimation() {
		if (containerRef.current && scrollerRef.current) {
			const scrollerContent = Array.from(scrollerRef.current.children);

			for (const item of scrollerContent) {
				const duplicatedItem = item.cloneNode(true);
				if (scrollerRef.current) {
					scrollerRef.current.appendChild(duplicatedItem);
				}
			}

			getDirection();
			getSpeed();
			setStart(true);
		}
	}
	const getDirection = () => {
		if (containerRef.current) {
			if (direction === "left") {
				containerRef.current.style.setProperty(
					"--animation-direction",
					"forwards",
				);
			} else {
				containerRef.current.style.setProperty(
					"--animation-direction",
					"reverse",
				);
			}
		}
	};
	const getSpeed = () => {
		if (containerRef.current) {
			if (speed === "fast") {
				containerRef.current.style.setProperty("--animation-duration", "20s");
			} else if (speed === "normal") {
				containerRef.current.style.setProperty("--animation-duration", "40s");
			} else {
				containerRef.current.style.setProperty("--animation-duration", "120s");
			}
		}
	};
	return (
		<div
			ref={containerRef}
			className={cn(
				"scroller relative z-20 max-w-7xl overflow-hidden [mask-image:linear-gradient(to_right,transparent,white_10%,white_90%,transparent)]",
				className,
			)}
		>
			<ul
				ref={scrollerRef}
				className={cn(
					"flex min-w-full shrink-0 gap-4 py-4 w-max flex-nowrap",
					start && "animate-scroll ",
					pauseOnHover && "hover:[animation-play-state:paused]",
				)}
			>
				{items.map((item) => (
					<li
						className="w-[350px] max-w-full relative rounded-2xl flex-shrink-0 shadow-lg ring-1 ring-gray-900/5 px-6 py-6 md:w-[450px] bg-white"
						key={`${item.author?.name}-${item.author?.handle}`}
					>
						<blockquote className="text-gray-900">
							<p>{`"${item.body}"`}</p>
							<figcaption className="mt-6 flex items-center gap-x-4">
								{item.author?.imageUrl && (
									<img
										alt={item.author?.name}
										src={item.author?.imageUrl}
										className="size-10 rounded-full bg-gray-50"
									/>
								)}
								<div>
									<div className="font-semibold flex items-center gap-x-1">
										{item.author?.name}
										{item.author?.xVerified && (
											<Icon
												kind="twitter-verified"
												size="xs"
												className="size-5"
											/>
										)}
									</div>
									<div className="text-gray-600">{item.author?.handle}</div>
								</div>
								{item.author?.logoUrl && (
									<img
										alt={`${item.author?.name}'s company logo`}
										src={item.author?.logoUrl}
										className="h-10 w-auto flex-none ml-auto"
									/>
								)}
							</figcaption>
						</blockquote>
					</li>
				))}
			</ul>
		</div>
	);
}
